import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { EXAM_TYPE_LABEL } from '@dru/types';
import type { CursorPage, ExaminationOrderRow } from '@dru/types';
import { Button, Card, CardHeader, EmptyState, ErrorState, IconAction, RowActions, SearchInput, StatusBadge, TextSkeleton, cn } from '@dru/ui';
import { Download, Eye, FileText } from 'lucide-react';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage } from '../../../lib/auth';
import { formatDate, formatDateTime } from '../../../lib/format';
import { openReport } from '../../lab/routes/Reports';
import { NotShared, isPolicyRefusal } from './HealthReports';
import { PdfViewerDialog } from './PdfViewerDialog';

interface IssuedRow { id: string; profileName: string; reportNumber: string; revision: number; stage: string; issuedAt: string }

/**
 * The issued clinical reports on one case, for HR — only when the company's
 * health report policy is FULL. The server refuses otherwise; the message it
 * gives is shown rather than an empty list that would look like "none".
 */
export function CompanyProfileReports({ orderId }: { orderId: string }) {
  // A policy refusal (SUMMARY or NONE) is a rule, not a failure: caught here
  // so it reads like the health report's own "not shared" notice.
  const list = useQuery<{ rows: IssuedRow[]; refused?: string }>(
    () => api.get<IssuedRow[]>(`/profile-reports/company/orders/${orderId}`)
      .then((rows) => ({ rows }))
      .catch((e: unknown) => { if (isPolicyRefusal(e)) return { rows: [], refused: errorMessage(e) }; throw e; }),
    [orderId]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<{ title: string; path: string; file: string } | null>(null);
  async function get(key: string, path: string) {
    setBusy(key); setError(null);
    try { await openReport(path, 'download'); } catch (e) { setError(errorMessage(e)); } finally { setBusy(null); }
  }
  if (list.loading && !list.data) return <TextSkeleton lines={3} />;
  if (list.error) return <p className="text-small text-neutral-500">{list.error}</p>;
  if (list.data?.refused) return <NotShared message={list.data.refused} />;
  const rows = list.data?.rows ?? [];
  // Told apart from the health check report above: that is one summary of
  // the whole checkup; these are the lab's signed PDFs, one per test profile.
  const note = <p className="text-caption text-neutral-600">All the laboratory's signed reports for this checkup, in one PDF.</p>;
  if (!rows.length) return <div className="space-y-1">{note}<p className="text-small text-neutral-500">No clinical reports have been issued on this examination yet.</p></div>;
  return (
    <div className="space-y-2">
      {note}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" icon={<Eye className="h-4 w-4" aria-hidden />}
          onClick={() => setViewing({ title: 'Health report', path: `/profile-reports/company/orders/${orderId}/download-all`, file: `health-report-${orderId.slice(0, 8)}` })}>
          View report
        </Button>
        <Button size="sm" variant="secondary" loading={busy === 'all'} icon={<Download className="h-4 w-4" aria-hidden />}
          onClick={() => void get('all', `/profile-reports/company/orders/${orderId}/download-all`)}>
          Download PDF
        </Button>
      </div>
      <details>
        <summary className="cursor-pointer text-small font-medium text-primary-600 hover:text-primary-700 max-md:min-h-10 max-md:py-2">
          View or download one report at a time ({rows.length})
        </summary>
      <ul className="-mx-4 divide-y divide-neutral-100">
        {rows.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 text-small text-neutral-900">
                <FileText className="h-4 w-4 shrink-0 text-neutral-500" aria-hidden />
                {r.profileName}
                <StatusBadge tone={r.stage === 'FINAL' ? 'covered' : 'info'}>{r.stage === 'FINAL' ? 'Final · physician reviewed' : 'Physician review pending'}</StatusBadge>
              </div>
              <div className="pl-6 text-caption text-neutral-600">{r.reportNumber} · revision {r.revision} · issued {formatDateTime(r.issuedAt)}</div>
            </div>
            <RowActions>
              <IconAction label={`View ${r.profileName}`} icon={Eye}
                onClick={() => setViewing({ title: r.profileName, path: `/profile-reports/company/orders/${orderId}/reports/${r.id}/download`, file: r.reportNumber })} />
              <IconAction label={`Download ${r.profileName} (PDF)`} icon={Download} loading={busy === r.id}
                onClick={() => void get(r.id, `/profile-reports/company/orders/${orderId}/reports/${r.id}/download`)} />
            </RowActions>
          </li>
        ))}
      </ul>
      </details>
      {error && <p role="alert" className="text-caption text-danger-600">{error}</p>}
      {viewing && <PdfViewerDialog title={viewing.title} grantPath={viewing.path} fileName={viewing.file} onClose={() => setViewing(null)} />}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */

/** Case states before any sample is taken: no lab report can exist yet. */
const BEFORE_LAB = new Set(['REQUESTED', 'ROUTED', 'CONSENT_PENDING', 'SCHEDULED', 'CANCELLED', 'EXPIRED']);

interface Checkup {
  order: ExaminationOrderRow;
  rows: IssuedRow[];
}
type Loaded = { checkups: Checkup[]; refused?: string };

/**
 * Each checkup's issued reports, fetched per case. One policy refusal answers
 * for all of them: the policy is the company's, not the case's.
 */
async function loadCheckups(orders: ExaminationOrderRow[]): Promise<Loaded> {
  const eligible = orders.filter((o) => !BEFORE_LAB.has(o.status));
  const out: Checkup[] = [];
  for (let i = 0; i < eligible.length; i += 6) {
    const batch = eligible.slice(i, i + 6);
    try {
      const lists = await Promise.all(batch.map((o) => api.get<IssuedRow[]>(`/profile-reports/company/orders/${o.id}`)));
      batch.forEach((o, k) => { if (lists[k]!.length) out.push({ order: o, rows: lists[k]! }); });
    } catch (e) {
      if (isPolicyRefusal(e)) return { checkups: [], refused: errorMessage(e) };
      throw e;
    }
  }
  return { checkups: out };
}

const examLabel = (t: string) => EXAM_TYPE_LABEL[t as keyof typeof EXAM_TYPE_LABEL] ?? t;

/** One checkup's lab reports, as a downloadable list. */
function CheckupReports({ c, showEmployee }: { c: Checkup; showEmployee?: boolean }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const o = c.order;
  async function get(key: string, path: string) {
    setBusy(key); setError(null);
    try { await openReport(path, 'download'); } catch (e) { setError(errorMessage(e)); } finally { setBusy(null); }
  }
  const final = c.rows.every((r) => r.stage === 'FINAL');
  const [viewing, setViewing] = useState<{ title: string; path: string; file: string } | null>(null);
  const allPath = `/profile-reports/company/orders/${o.id}/download-all`;
  const who = o.employee ? `${o.employee.fullName} (${o.employee.employeeCode})` : 'Employee';
  return (
    <li className="px-5 py-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          {showEmployee && o.employee && (
            <Link to={`/hr/employees/${o.employeeId}`} className="font-medium text-neutral-900 hover:text-primary-700">
              {o.employee.fullName} <span className="font-mono text-caption text-neutral-500">{o.employee.employeeCode}</span>
            </Link>
          )}
          <div className={cn('text-small', showEmployee ? 'text-neutral-600' : 'font-medium text-neutral-900')}>
            {examLabel(o.examinationType)} · case raised {formatDate(o.createdAt)} · reports issued {formatDate(c.rows[0]!.issuedAt)}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2">
            <StatusBadge tone={final ? 'covered' : 'info'}>{final ? 'Final' : 'Physician review pending'}</StatusBadge>
            <Link to={`/hr/examinations/${o.id}`} className="text-caption text-primary-600 hover:text-primary-700">Open case</Link>
          </div>
        </div>
        {/* One PDF: every report, each one's pages after the last. */}
        <div className="flex flex-wrap gap-2">
          <Button size="sm" icon={<Eye className="h-4 w-4" aria-hidden />}
            onClick={() => setViewing({ title: `Health report — ${who}`, path: allPath, file: `health-report-${o.employee?.employeeCode ?? o.id.slice(0, 8)}` })}>
            View report
          </Button>
          <Button size="sm" variant="secondary" loading={busy === 'all'} icon={<Download className="h-4 w-4" aria-hidden />}
            onClick={() => void get('all', allPath)}>
            Download PDF
          </Button>
        </div>
      </div>
      <p className="mt-1 text-caption text-neutral-600">
        One PDF with all {c.rows.length} report{c.rows.length === 1 ? '' : 's'}: {c.rows.map((r) => r.profileName).join(', ')}.
      </p>
      <details className="mt-2">
        <summary className="cursor-pointer text-small font-medium text-primary-600 hover:text-primary-700 max-md:min-h-10 max-md:py-2">
          View or download one report at a time
        </summary>
      <ul className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        {c.rows.map((r) => (
          <li key={r.id} className="flex min-w-0 items-center justify-between gap-2 rounded-btn border border-neutral-200 px-3 py-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 truncate text-small text-neutral-900">
                <FileText className="h-4 w-4 shrink-0 text-neutral-500" aria-hidden />{r.profileName}
              </div>
              <div className="truncate pl-[1.375rem] text-caption text-neutral-500">{r.reportNumber} · rev {r.revision}</div>
            </div>
            <RowActions>
              <IconAction label={`View ${r.profileName}`} icon={Eye}
                onClick={() => setViewing({ title: r.profileName, path: `/profile-reports/company/orders/${o.id}/reports/${r.id}/download`, file: r.reportNumber })} />
              <IconAction label={`Download ${r.profileName} (PDF)`} icon={Download} loading={busy === r.id}
                onClick={() => void get(r.id, `/profile-reports/company/orders/${o.id}/reports/${r.id}/download`)} />
            </RowActions>
          </li>
        ))}
      </ul>
      </details>
      {error && <p role="alert" className="mt-2 text-caption text-danger-600">{error}</p>}
      {viewing && <PdfViewerDialog title={viewing.title} grantPath={viewing.path} fileName={viewing.file} onClose={() => setViewing(null)} />}
    </li>
  );
}

const LAB_HINT = 'The laboratory\'s signed reports, one per test group (CBC, liver, kidney…), each prepared, verified and authorised. Shown only when your company shares full reports with HR.';

/** The employee profile's Health reports tab: every checkup's signed lab reports. */
export function EmployeeLabReports({ employeeId }: { employeeId: string }) {
  const data = useQuery<Loaded>(async () => {
    const page = await api.get<CursorPage<ExaminationOrderRow>>(`/examinations?employeeId=${employeeId}&limit=50`);
    return loadCheckups(page.items);
  }, [employeeId]);
  return (
    <Card>
      <CardHeader title="Health reports" hint={LAB_HINT} />
      <LabReportsBody data={data} empty="No lab report has been issued for this employee yet. Reports appear once the laboratory releases results." />
    </Card>
  );
}

/** The Reports page's register: the latest checkups across the company with their lab reports. */
export function LabReportsRegister() {
  const data = useQuery<Loaded>(async () => {
    const pages = await Promise.all(['CERTIFIED', 'REVIEW_PENDING', 'RETEST_REQUIRED', 'LAB_PENDING'].map((st) =>
      api.get<CursorPage<ExaminationOrderRow>>(`/examinations?status=${st}&limit=50`)));
    const orders = pages.flatMap((p) => p.items).sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 60);
    return loadCheckups(orders);
  }, []);
  const [q, setQ] = useState('');
  const term = q.trim().toLowerCase();
  const filtered = useMemo<UseQueryLike>(() => {
    if (!data.data || !term) return data;
    return { ...data, data: { ...data.data, checkups: data.data.checkups.filter((c) =>
      `${c.order.employee?.fullName ?? ''} ${c.order.employee?.employeeCode ?? ''} ${c.rows.map((r) => r.reportNumber).join(' ')}`
        .toLowerCase().includes(term)) } };
  }, [data, term]);
  return (
    <Card>
      <CardHeader title="Health reports" hint={`${LAB_HINT} Latest 60 checkups.`} />
      {!data.data?.refused && (
        <div className="px-5 pb-3">
          <SearchInput value={q} onValueChange={setQ} placeholder="Search employee or report number" className="max-w-sm" />
        </div>
      )}
      <LabReportsBody data={filtered} showEmployee
        empty={term ? 'No checkup matches that search.' : 'No lab report has been issued yet. Reports appear once the laboratory releases results.'} />
    </Card>
  );
}

type UseQueryLike = { data?: Loaded | null; loading: boolean; error?: string | null; reload: () => void };

function LabReportsBody({ data, empty, showEmployee }: { data: UseQueryLike; empty: string; showEmployee?: boolean }) {
  if (data.error) return <div className="px-5 pb-4"><ErrorState title="Reports could not be loaded" detail={data.error} onRetry={data.reload} /></div>;
  if (data.loading && !data.data) return <div className="px-5 pb-4"><TextSkeleton lines={3} /></div>;
  if (data.data?.refused) return <div className="px-5 pb-4"><NotShared message={data.data.refused} /></div>;
  const checkups = data.data?.checkups ?? [];
  if (!checkups.length) return <div className="px-5 pb-4"><EmptyState title="No reports yet" hint={empty} /></div>;
  return (
    <ul className="divide-y divide-neutral-100 border-t border-neutral-100">
      {checkups.map((c) => <CheckupReports key={c.order.id} c={c} showEmployee={showEmployee} />)}
    </ul>
  );
}
